import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

export default function MenuManagement() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [catModal, setCatModal] = useState(false);
  const [itemModal, setItemModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");

  const [catForm, setCatForm] = useState({ category_name: "", display_order: 0, itemizer_id: 1 });
  const [itemForm, setItemForm] = useState({
    item_name: "", description: "", unit_price: 0,
    category_id: "", tax_rate_ids: [], image_url: "", barcode: "", display_order: 0
  });

  const fetchAll = useCallback(async () => {
    const [c, i, t] = await Promise.all([
      api.get("/admin/categories"), api.get("/admin/items"), api.get("/admin/taxes")
    ]);
    setCategories(c.data); setItems(i.data); setTaxes(t.data);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Categories
  const openCatModal = (cat = null) => {
    setEditingCat(cat);
    setCatForm(cat ? { category_name: cat.category_name, display_order: cat.display_order || 0, itemizer_id: cat.itemizer_id || 1 }
      : { category_name: "", display_order: categories.length, itemizer_id: 1 });
    setCatModal(true);
  };

  const saveCat = async () => {
    try {
      if (editingCat) {
        await api.put(`/admin/categories/${editingCat.id}`, catForm);
      } else {
        await api.post("/admin/categories", catForm);
      }
      toast.success(editingCat ? "Category updated" : "Category created");
      setCatModal(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleCat = async (cat) => {
    await api.delete(`/admin/categories/${cat.id}`);
    toast.success("Category deactivated"); fetchAll();
  };

  // Items
  const openItemModal = (item = null) => {
    setEditingItem(item);
    setItemForm(item ? {
      item_name: item.item_name, description: item.description || "",
      unit_price: item.unit_price,
      category_id: item.category_id || "", tax_rate_ids: item.tax_rate_ids || [],
      image_url: item.image_url || "", barcode: item.barcode || "", display_order: item.display_order || 0
    } : {
      item_name: "", description: "", unit_price: 0,
      category_id: categories[0]?.id || "", tax_rate_ids: [], image_url: "", barcode: "", display_order: items.length
    });
    setItemModal(true);
  };

  const saveItem = async () => {
    try {
      if (editingItem) {
        await api.put(`/admin/items/${editingItem.id}`, itemForm);
      } else {
        await api.post("/admin/items", itemForm);
      }
      toast.success(editingItem ? "Item updated" : "Item created");
      setItemModal(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleItem = async (item) => {
    await api.put(`/admin/items/${item.id}/toggle`);
    toast.success(`Item ${item.is_active ? "deactivated" : "activated"}`); fetchAll();
  };

  const filteredItems = useMemo(() => items.filter((i) => {
    if (filterCat !== "all" && i.category_id !== filterCat) return false;
    if (search && !i.item_name.toLowerCase().includes(search.toLowerCase()) &&
        !i.item_code.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, filterCat, search]);

  const getCatName = useCallback((id) => categories.find((c) => c.id === id)?.category_name || "—", [categories]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="menu-management">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Menu Management</h1>
      <Tabs defaultValue="items" className="w-full">
        <TabsList className="rounded-none border border-gray-200 bg-white p-0 h-auto">
          <TabsTrigger value="categories" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-6 py-2 text-xs font-bold uppercase tracking-wider" data-testid="tab-categories">
            Categories
          </TabsTrigger>
          <TabsTrigger value="items" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-6 py-2 text-xs font-bold uppercase tracking-wider" data-testid="tab-items">
            Items
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openCatModal()} className="rounded-none bg-black text-white hover:bg-gray-800 text-xs h-9" data-testid="add-category-button">
              <Plus className="w-3 h-3 mr-1" /> Add Category
            </Button>
          </div>
          <div className="bg-white border border-gray-200">
            <table className="w-full table-dense">
              <thead><tr className="border-b border-gray-100">
                <th className="text-left">Name</th><th className="text-center">Itemizer</th><th className="text-center">Order</th><th className="text-center">Status</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="font-semibold">{cat.category_name}</td>
                    <td className="text-center"><span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-gray-100">{cat.itemizer_id === 1 ? 'S1 Food' : cat.itemizer_id === 2 ? 'S2 Bev' : 'S3 Merch'}</span></td>
                    <td className="text-center">{cat.display_order}</td>
                    <td className="text-center">
                      <span className={`text-[10px] font-bold uppercase ${cat.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                        {cat.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="text-right space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openCatModal(cat)} className="h-7 text-xs" data-testid={`edit-cat-${cat.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search items..." className="pl-9 rounded-none border-gray-300 h-9 text-sm"
                data-testid="item-search-input" />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-48 rounded-none h-9 text-sm" data-testid="category-filter-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => openItemModal()} className="rounded-none bg-black text-white hover:bg-gray-800 text-xs h-9" data-testid="add-item-button">
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>
          <div className="bg-white border border-gray-200">
            <table className="w-full table-dense">
              <thead><tr className="border-b border-gray-100">
                <th className="text-left">Code</th><th className="text-left">Name</th><th className="text-left">Category</th>
                <th className="text-right">Price</th><th className="text-center">Active</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="font-mono text-xs">{item.item_code}</td>
                    <td className="font-medium">{item.item_name}</td>
                    <td className="text-gray-500 text-sm">{getCatName(item.category_id)}</td>
                    <td className="text-right font-mono">${item.unit_price.toFixed(2)}</td>
                    <td className="text-center">
                      <Switch checked={item.is_active} onCheckedChange={() => toggleItem(item)} data-testid={`toggle-item-${item.id}`} />
                    </td>
                    <td className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openItemModal(item)} className="h-7 text-xs" data-testid={`edit-item-${item.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Modal */}
      <Dialog open={catModal} onOpenChange={setCatModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editingCat ? "Edit" : "New"} Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Name</Label>
              <Input value={catForm.category_name} onChange={(e) => setCatForm({ ...catForm, category_name: e.target.value })}
                className="mt-1 rounded-none" data-testid="cat-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Itemizer ID</Label>
              <Select value={String(catForm.itemizer_id)} onValueChange={(v) => setCatForm({ ...catForm, itemizer_id: parseInt(v) })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="cat-itemizer-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="1">1 — Food (S1)</SelectItem>
                  <SelectItem value="2">2 — Beverages (S2)</SelectItem>
                  <SelectItem value="3">3 — Merchandise (S3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Display Order</Label>
              <Input type="number" value={catForm.display_order} onChange={(e) => setCatForm({ ...catForm, display_order: parseInt(e.target.value) || 0 })}
                className="mt-1 rounded-none" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCat} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="save-category-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Modal */}
      <Dialog open={itemModal} onOpenChange={setItemModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingItem ? "Edit" : "New"} Item</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Name</Label>
              <Input value={itemForm.item_name} onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })}
                className="mt-1 rounded-none" data-testid="item-name-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Description</Label>
              <Input value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                className="mt-1 rounded-none" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Price</Label>
              <Input type="number" step="0.01" value={itemForm.unit_price}
                onChange={(e) => setItemForm({ ...itemForm, unit_price: parseFloat(e.target.value) || 0 })}
                className="mt-1 rounded-none" data-testid="item-price-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Category</Label>
              <Select value={itemForm.category_id} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="item-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {categories.filter(c => c.is_active).map((c) => <SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Barcode</Label>
              <Input value={itemForm.barcode} onChange={(e) => setItemForm({ ...itemForm, barcode: e.target.value })}
                className="mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-2 block">Tax Rates</Label>
              <div className="flex flex-wrap gap-3">
                {taxes.filter(t => t.is_active).map((tax) => (
                  <label key={tax.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={itemForm.tax_rate_ids.includes(tax.id)}
                      onCheckedChange={(checked) => {
                        setItemForm(prev => ({
                          ...prev,
                          tax_rate_ids: checked
                            ? [...prev.tax_rate_ids, tax.id]
                            : prev.tax_rate_ids.filter(id => id !== tax.id)
                        }));
                      }}
                    />
                    {tax.tax_name} ({tax.rate_percent}%)
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveItem} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="save-item-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
